import { z } from 'zod';

/** One scalar row of the reference_designs catalog (list view — no jsonb blobs). */
export const ReferenceDesignListItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  source: z.string().nullable(),
  patent_id: z.string().nullable(),
  elements: z.number().nullable(),
  powered_elements: z.number().nullable(),
  efl_mm: z.number().nullable(),
  fno: z.number().nullable(),
  hfov_deg: z.number().nullable(),
  spot_rms_onaxis_um: z.number().nullable(),
  strehl_onaxis: z.number().nullable(),
  metrics_status: z.string().nullable(),
  is_template: z.boolean(),
  created_at: z.string().nullable(),
});
export type ReferenceDesignListItem = z.infer<typeof ReferenceDesignListItemSchema>;

export const ReferenceDesignListSchema = z.object({
  designs: z.array(ReferenceDesignListItemSchema),
  total: z.number(),
  /** Distinct source values for the filter dropdown (NULL = untagged rows). */
  sources: z.array(z.string().nullable()),
});
export type ReferenceDesignList = z.infer<typeof ReferenceDesignListSchema>;

/** Full scalar column set for the detail/viewer page. */
export const ReferenceDesignDetailFieldsSchema = ReferenceDesignListItemSchema.extend({
  tags: z.array(z.string()).nullable(),
  glass_catalogs: z.array(z.string()).nullable(),
  wavelengths_um: z.array(z.number()).nullable(),
  lambda_primary_um: z.number().nullable(),
  field_type: z.string().nullable(),
  pupil_mode: z.string().nullable(),
  na_image: z.number().nullable(),
  epd_mm: z.number().nullable(),
  img_half_height_mm: z.number().nullable(),
  bfl_mm: z.number().nullable(),
  total_track_mm: z.number().nullable(),
  working_distance_mm: z.number().nullable(),
  groups: z.number().nullable(),
  asphere_count: z.number().nullable(),
  has_model_glass: z.boolean().nullable(),
  is_reflective: z.boolean().nullable(),
  is_finite_conjugate: z.boolean().nullable(),
  spot_rms_max_um: z.number().nullable(),
  wfe_rms_onaxis_waves: z.number().nullable(),
  mtf_cutoff_lpmm: z.number().nullable(),
  distortion_max_pct: z.number().nullable(),
  metrics_error: z.string().nullable(),
  metrics_computed_at: z.string().nullable(),
  patent_expired: z.boolean().nullable(),
  patent_expired_basis: z.string().nullable(),
  patent_legal_status: z.string().nullable(),
  patent_status_as_of: z.string().nullable(),
});
export type ReferenceDesignDetailFields = z.infer<typeof ReferenceDesignDetailFieldsSchema>;

export const ReferenceDesignDetailSchema = z.object({
  design: ReferenceDesignDetailFieldsSchema,
  /** Raw stored prescription, verbatim — for the download button. */
  llm_json: z.unknown(),
  /** Normalized `{system, surfaces, schema_version}` payload ready to POST to
   *  the analysis-service, or null when normalization failed. */
  system: z.unknown(),
  normalize_error: z.string().nullable(),
});
export type ReferenceDesignDetail = z.infer<typeof ReferenceDesignDetailSchema>;

/** A patent-corpus release table present on this database. */
export const ReferenceDesignReleaseSchema = z.object({
  table: z.string(),
  /** "1.1.0" for patent_designs_v1_1_0. */
  version: z.string(),
  /** pg_class.reltuples; null when the table has never been analyzed. */
  estimated_rows: z.number().nullable(),
  /** Lifecycle stamp scripts/corpusRelease.ts writes as the table COMMENT; null when unstamped. */
  state: z.enum(['building', 'verified', 'published']).nullable(),
  stamped_at: z.string().nullable(),
});

/** Observability snapshot over public.reference_designs for the admin health page. */
export const ReferenceDesignHealthSchema = z.object({
  computed_at: z.string(),
  relation: z.object({
    /** 'table' on a database built from migrations/; 'view' on the hosted UNION ALL shape. */
    kind: z.enum(['table', 'view']),
    /** The relations that hold rows, with exact counts (the table itself, or the view's base tables). */
    base_tables: z.array(z.object({ name: z.string(), rows: z.number() })),
    /** Release table the view serves; null on the table shape. */
    active_release_table: z.string().nullable(),
    /** Release table the view's ATTACHED and ENABLED INSTEAD OF UPDATE trigger routes to; null when no such
     *  trigger exists (a function left behind by a dropped trigger does not count). Should equal active_release_table. */
    update_trigger_table: z.string().nullable(),
    releases: z.array(ReferenceDesignReleaseSchema),
  }),
  rows: z.object({
    total: z.number(),
    by_corpus: z.array(z.object({ source: z.string().nullable(), version: z.string().nullable(), rows: z.number() })),
    by_metrics_status: z.array(z.object({ status: z.string().nullable(), rows: z.number() })),
  }),
  columns: z.array(z.object({
    name: z.string(),
    data_type: z.string(),
    null_rows: z.number(),
    null_pct: z.number(),
    /** When NULL is expected for this column (src/services/referenceDesigns/nullExpectations.ts); null = unclassified. */
    rule: z.string().nullable(),
    /** Rows in the wrong state for this column under `rule`; null when unclassified. */
    unexpected_null_rows: z.number().nullable(),
    /** The same figures per corpus (`source` value), largest corpus first. */
    by_source: z.array(z.object({
      source: z.string().nullable(),
      rows: z.number(),
      null_rows: z.number(),
      unexpected_null_rows: z.number().nullable(),
    })),
  })),
  migrations: z.object({
    /** false on a database the untracked runner migrated (no public.schema_migrations). */
    ledger_exists: z.boolean(),
    /** Every migrations/*.sql this build ships, with when the ledger says it was applied here. */
    in_tree: z.array(z.object({ filename: z.string(), applied_at: z.string().nullable() })),
    /** Ledger rows this build has no file for (another branch, or renumbered since). */
    applied_not_in_tree: z.array(z.object({ filename: z.string(), applied_at: z.string() })),
  }),
});
export type ReferenceDesignHealth = z.infer<typeof ReferenceDesignHealthSchema>;
